import { useCallback } from 'react';

import { useLazyGetShipmentBalancesQuery } from '../api/receiveApi';
import { normalizeBalanceRows } from '../lib/normalize';
import type { ReceiveTableRow } from './types';

export interface UseShipmentBalancesOptions {
  projectId?: number;
  siteId?: number;
  /** Sole error channel — the returned promise always resolves, callers don't need try/catch */
  onError?: (error: unknown) => void;
}

export interface FetchShipmentBalancesArgs {
  shipmentId: number;
  /** Consignment row the lines belong to (their `parentId`). */
  rowId: string;
  /** Level of that row; lines are nested one below it. */
  level: number;
}

/**
 * Fetches a shipment's per-line incoming balances when its consignment row is
 * expanded — the deepest level of the dashboard — and returns them already
 * shaped as table rows. Same wrapper contract as `useLocationShipments`.
 *
 * Resolves to `null` when the request failed (the error went to `onError`),
 * which callers distinguish from an empty list.
 */
export const useShipmentBalances = ({ projectId, siteId, onError }: UseShipmentBalancesOptions = {}) => {
  const [fetchBalances, { isFetching, isError, error }] = useLazyGetShipmentBalancesQuery();

  const fetchShipmentBalances = useCallback(
    async ({ shipmentId, rowId, level }: FetchShipmentBalancesArgs): Promise<ReceiveTableRow[] | null> => {
      try {
        const response = await fetchBalances({ shipmentId, projectId, siteId }).unwrap();

        return normalizeBalanceRows(rowId, response, level);
      } catch (error) {
        onError?.(error);
        return null;
      }
    },
    [fetchBalances, projectId, siteId, onError]
  );

  return {
    fetchShipmentBalances,
    // Scoped to the MOST RECENT request — see the note in `useLocationShipments`.
    isFetching,
    isError,
    error
  };
};
